import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Cloud, DeviceMobile, Plus, ArrowsClockwise } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useStorageMode } from '@/hooks/useStorageMode';
import { exportLocalData, clearLocalDatabase, localDb } from '@/lib/localDatabase';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useUnifiedTransactions } from '@/hooks/useUnifiedData';
import { clearSyncAfterAuthState, getSyncAfterAuthState } from '@/lib/syncAfterAuth';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog';

type ConflictResolution = 'merge' | 'replace_local' | 'replace_cloud';

interface LocalDataCounts {
  transactions: number;
  categories: number;
}

export function PostAuthSync() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { setMode, isAuthenticated, cloudUser } = useStorageMode();
  const { fetchTransactions, fetchCategories } = useUnifiedTransactions();

  // Prevent double-trigger for the same sync request.
  // (This component is mounted at app root, so we must allow *new* attempts.)
  const lastHandledNonceRef = useRef<string | null>(null);
  
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [hasCloudData, setHasCloudData] = useState(false);
  const [localDataCount, setLocalDataCount] = useState<LocalDataCounts>({ transactions: 0, categories: 0 });
  const [cloudDataCount, setCloudDataCount] = useState({ transactions: 0 });

  const checkLocalData = useCallback(async (): Promise<LocalDataCounts> => {
    const transactions = await localDb.transactions.getAll();
    const categories = await localDb.categories.getAll();
    const counts = { transactions: transactions.length, categories: categories.length };
    setLocalDataCount(counts);
    return counts;
  }, []);

  const checkSyncAfterAuth = useCallback(async () => {
    // New simplified logic: if user is authenticated but NOT in cloud mode, trigger sync
    if (!isAuthenticated || !cloudUser) return;
    
    // Get current storage mode
    const currentMode = localStorage.getItem('axis_storage_mode');
    
    // If already in cloud mode, nothing to do
    if (currentMode === 'cloud') return;
    
    // Generate a session-based nonce to prevent duplicate handling
    const sessionNonce = `${cloudUser.id}_${Date.now()}`;
    if (lastHandledNonceRef.current && lastHandledNonceRef.current.startsWith(cloudUser.id)) {
      // Already handled for this user in this session
      return;
    }
    
    lastHandledNonceRef.current = sessionNonce;

    try {
      // Check both local and cloud data
      const [localData, cloudResult] = await Promise.all([
        checkLocalData(),
        supabase.from('transactions').select('id').limit(100),
      ]);

      const cloudTxCount = cloudResult.data?.length || 0;
      setCloudDataCount({ transactions: cloudTxCount });
      setHasCloudData(cloudTxCount > 0);

      // If local has data, show the dialog
      if (localData.transactions > 0) {
        setShowSyncDialog(true);
      } else {
        // No local data - just switch to cloud mode
        setMode('cloud');
        toast({
          title: t('sync.switchedToCloud', 'Переключено на облако'),
          description: t('sync.usingCloudData', 'Теперь используются данные из облака'),
        });
      }

      // Clear any legacy sync flags
      clearSyncAfterAuthState();
    } catch (e) {
      // Allow retry on error
      lastHandledNonceRef.current = null;
      toast({
        title: t('common.error', 'Ошибка'),
        description: t('sync.syncFailed', 'Не удалось переключиться на облако. Попробуйте ещё раз.'),
        variant: 'destructive',
      });
    }
  }, [checkLocalData, cloudUser, isAuthenticated, setMode, t, toast]);

  // Check if we need to show sync dialog after auth
  useEffect(() => {
    checkSyncAfterAuth();
  }, [checkSyncAfterAuth]);

  // Allow triggering sync dialog when user is already logged in (e.g. returned to /account)
  useEffect(() => {
    const handler = () => {
      checkSyncAfterAuth();
    };
    window.addEventListener('triggerPostAuthSync', handler);
    return () => window.removeEventListener('triggerPostAuthSync', handler);
  }, [checkSyncAfterAuth]);

  const performSync = useCallback(async (resolution: ConflictResolution) => {
    if (!cloudUser) return;
    
    setIsSyncing(true);
    setShowSyncDialog(false);

    try {
      if (resolution === 'replace_local') {
        // Just switch to cloud mode, local data stays on device
        setMode('cloud');
        
        // Invalidate all queries to force refetch from cloud
        await queryClient.invalidateQueries({ queryKey: ['transactions'] });
        await queryClient.invalidateQueries({ queryKey: ['categories'] });
        await queryClient.invalidateQueries({ queryKey: ['tags'] });
        await queryClient.invalidateQueries({ queryKey: ['profile'] });
        await queryClient.invalidateQueries({ queryKey: ['budgets'] });
        
        // Force immediate data refresh for the main page
        setTimeout(() => {
          fetchTransactions();
          fetchCategories();
        }, 100);
        
        toast({
          title: t('sync.switchedToCloud', 'Переключено на облако'),
          description: t('sync.usingCloudData', 'Теперь используются данные из облака'),
        });
        setIsSyncing(false);
        return;
      }

      const localData = await exportLocalData();
      const userId = cloudUser.id;

      if (resolution === 'replace_cloud' || resolution === 'merge') {
        // Upload local data to cloud
        
        // Map old IDs to new IDs for relationships
        const categoryIdMap: Record<string, string> = {};
        const tagIdMap: Record<string, string> = {};

        // Upload categories (parent first, then children)
        if (localData.categories && localData.categories.length > 0) {
          const parentCategories = localData.categories.filter(c => !c.parent_id);
          for (const cat of parentCategories) {
            const { data: newCat } = await supabase
              .from('categories')
              .insert({
                user_id: userId,
                name: cat.name,
                icon: cat.icon,
                color: cat.color,
                type: cat.type,
                parent_id: null,
              })
              .select()
              .single();
            
            if (newCat) {
              categoryIdMap[cat.id] = newCat.id;
            }
          }

          const childCategories = localData.categories.filter(c => c.parent_id);
          for (const cat of childCategories) {
            const newParentId = categoryIdMap[cat.parent_id!] || null;
            const { data: newCat } = await supabase
              .from('categories')
              .insert({
                user_id: userId,
                name: cat.name,
                icon: cat.icon,
                color: cat.color,
                type: cat.type,
                parent_id: newParentId,
              })
              .select()
              .single();
            
            if (newCat) {
              categoryIdMap[cat.id] = newCat.id;
            }
          }
        }

        // Upload tags
        if (localData.tags && localData.tags.length > 0) {
          for (const tag of localData.tags) {
            const { data: newTag } = await supabase
              .from('tags')
              .insert({
                user_id: userId,
                name: tag.name,
                color: tag.color,
              })
              .select()
              .single();
            
            if (newTag) {
              tagIdMap[tag.id] = newTag.id;
            }
          }
        }

        // Upload transactions
        if (localData.transactions && localData.transactions.length > 0) {
          const transactionIdMap: Record<string, string> = {};
          
          for (const tx of localData.transactions) {
            const newCategoryId = tx.category_id ? categoryIdMap[tx.category_id] : null;
            
            const { data: newTx } = await supabase
              .from('transactions')
              .insert({
                user_id: userId,
                type: tx.type,
                amount: tx.amount,
                description: tx.description,
                category_id: newCategoryId,
                date: tx.date,
              })
              .select()
              .single();
            
            if (newTx) {
              transactionIdMap[tx.id] = newTx.id;
            }
          }

          // Upload transaction tags
          if (localData.transactionTags && localData.transactionTags.length > 0) {
            for (const tt of localData.transactionTags) {
              const newTxId = transactionIdMap[tt.transaction_id];
              const newTagId = tagIdMap[tt.tag_id];
              
              if (newTxId && newTagId) {
                await supabase
                  .from('transaction_tags')
                  .insert({
                    transaction_id: newTxId,
                    tag_id: newTagId,
                  });
              }
            }
          }
        }

        // Upload budgets
        if (localData.budgets && localData.budgets.length > 0) {
          for (const budget of localData.budgets) {
            const newCategoryId = budget.category_id ? categoryIdMap[budget.category_id] : null;
            
            await supabase
              .from('budgets')
              .insert({
                user_id: userId,
                category_id: newCategoryId,
                amount: budget.amount,
                month: budget.month,
                year: budget.year,
                period_type: budget.period_type,
              });
          }
        }

        // Update profile
        if (localData.profile) {
          await supabase
            .from('profiles')
            .upsert({
              user_id: userId,
              currency: localData.profile.currency,
              wallet_name: localData.profile.wallet_name,
              initial_balance: localData.profile.initial_balance,
              week_start: localData.profile.week_start,
              first_day_of_month: localData.profile.first_day_of_month,
              default_period: localData.profile.default_period,
              round_amounts: localData.profile.round_amounts,
            }, { onConflict: 'user_id' });
        }
      }

      // Switch to cloud mode
      setMode('cloud');
      
      // Clear local data after successful sync
      await clearLocalDatabase();

      // Invalidate all queries to force refetch from cloud
      await queryClient.invalidateQueries({ queryKey: ['transactions'] });
      await queryClient.invalidateQueries({ queryKey: ['categories'] });
      await queryClient.invalidateQueries({ queryKey: ['tags'] });
      await queryClient.invalidateQueries({ queryKey: ['profile'] });
      await queryClient.invalidateQueries({ queryKey: ['budgets'] });
      
      // Force immediate data refresh for the main page
      setTimeout(() => {
        fetchTransactions();
        fetchCategories();
      }, 100);

      toast({
        title: t('sync.success', 'Успешно'),
        description: t('sync.dataSynced', 'Данные синхронизированы с облаком'),
      });
    } catch (error) {
      console.error('Error syncing data:', error);
      toast({
        title: t('sync.error', 'Ошибка'),
        description: t('sync.syncFailed', 'Не удалось синхронизировать данные'),
        variant: 'destructive',
      });
    } finally {
      setIsSyncing(false);
    }
  }, [cloudUser, setMode, t, toast, queryClient]);

  // Don't render anything if dialog is not shown and not syncing
  if (!showSyncDialog && !isSyncing) {
    return null;
  }

  return (
    <>
      {/* Syncing overlay */}
      <AnimatePresence>
        {isSyncing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="flex flex-col items-center gap-4"
            >
              <ArrowsClockwise className="w-12 h-12 text-primary animate-spin" />
              <p className="text-lg font-medium">{t('sync.syncing', 'Синхронизация...')}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sync options dialog */}
      <AlertDialog open={showSyncDialog} onOpenChange={setShowSyncDialog}>
      <AlertDialogContent 
          className="max-w-[calc(100vw-3rem)] sm:max-w-md rounded-3xl border-0 p-6"
          style={{
            background: 'linear-gradient(145deg, hsl(var(--card) / 0.85) 0%, hsl(var(--card) / 0.75) 100%)',
            backdropFilter: 'blur(64px) saturate(200%)',
            WebkitBackdropFilter: 'blur(64px) saturate(200%)',
            boxShadow: `
              inset 0 1px 1px 0 hsl(var(--foreground) / 0.08),
              inset 0 -1px 1px 0 hsl(var(--background) / 0.1),
              0 25px 50px -12px hsl(var(--foreground) / 0.25)
            `,
            border: '1px solid hsl(var(--border) / 0.4)',
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-3 text-lg font-semibold">
              <div 
                className="w-10 h-10 rounded-2xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(145deg, hsl(var(--primary) / 0.2) 0%, hsl(var(--primary) / 0.1) 100%)',
                  boxShadow: 'inset 0 1px 1px 0 hsl(var(--foreground) / 0.05)',
                }}
              >
                <Cloud className="w-5 h-5 text-primary" weight="duotone" />
              </div>
              {t('sync.conflictTitle', 'Обнаружены данные')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left text-sm text-muted-foreground pt-2">
              {hasCloudData ? (
                <>
                  {t('sync.conflictDescription', 'У вас есть данные и на устройстве, и в облаке. Что сделать?')}
                  <div 
                    className="mt-4 p-3 rounded-2xl space-y-2"
                    style={{
                      background: 'hsl(var(--muted) / 0.4)',
                      border: '1px solid hsl(var(--border) / 0.3)',
                    }}
                  >
                    <p className="flex items-center gap-2 text-xs">
                      <DeviceMobile className="w-4 h-4 text-amber-500" weight="duotone" />
                      <span>{t('sync.localTransactions', { count: localDataCount.transactions, defaultValue: `На устройстве: ${localDataCount.transactions} транзакций` })}</span>
                    </p>
                    <p className="flex items-center gap-2 text-xs">
                      <Cloud className="w-4 h-4 text-emerald-500" weight="duotone" />
                      <span>{t('sync.cloudTransactions', { count: cloudDataCount.transactions, defaultValue: `В облаке: ${cloudDataCount.transactions} транзакций` })}</span>
                    </p>
                  </div>
                </>
              ) : (
                t('sync.uploadLocalData', 'У вас есть локальные данные. Загрузить их в облако?')
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-3 pt-4">
            <button
              className="w-full text-left p-4 rounded-2xl flex items-start gap-4 transition-all active:scale-[0.98]"
              style={{
                background: 'linear-gradient(145deg, hsl(var(--muted) / 0.5) 0%, hsl(var(--muted) / 0.3) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid hsl(var(--border) / 0.3)',
                boxShadow: 'inset 0 1px 1px 0 hsl(var(--foreground) / 0.03)',
              }}
              onClick={() => performSync('merge')}
            >
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{
                  background: 'linear-gradient(145deg, hsl(var(--primary) / 0.15) 0%, hsl(var(--primary) / 0.08) 100%)',
                }}
              >
                <Plus className="w-5 h-5 text-primary" />
              </div>
              <div>
                <div className="font-medium text-foreground">{t('sync.merge', 'Объединить')}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {t('sync.mergeDescription', 'Добавить локальные данные к облачным')}
                </div>
              </div>
            </button>
            
            {hasCloudData && (
              <button
                className="w-full text-left p-4 rounded-2xl flex items-start gap-4 transition-all active:scale-[0.98]"
                style={{
                  background: 'linear-gradient(145deg, hsl(var(--muted) / 0.5) 0%, hsl(var(--muted) / 0.3) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid hsl(var(--border) / 0.3)',
                  boxShadow: 'inset 0 1px 1px 0 hsl(var(--foreground) / 0.03)',
                }}
                onClick={() => performSync('replace_local')}
              >
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{
                    background: 'linear-gradient(145deg, hsl(160 84% 39% / 0.15) 0%, hsl(160 84% 39% / 0.08) 100%)',
                  }}
                >
                  <Cloud className="w-5 h-5 text-emerald-500" weight="duotone" />
                </div>
                <div>
                  <div className="font-medium text-foreground">{t('sync.useCloud', 'Использовать облачные')}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {t('sync.useCloudDescription', 'Локальные данные останутся на устройстве')}
                  </div>
                </div>
              </button>
            )}
            
            {hasCloudData && (
              <button
                className="w-full text-left p-4 rounded-2xl flex items-start gap-4 transition-all active:scale-[0.98]"
                style={{
                  background: 'linear-gradient(145deg, hsl(var(--muted) / 0.5) 0%, hsl(var(--muted) / 0.3) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid hsl(var(--border) / 0.3)',
                  boxShadow: 'inset 0 1px 1px 0 hsl(var(--foreground) / 0.03)',
                }}
                onClick={() => performSync('replace_cloud')}
              >
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{
                    background: 'linear-gradient(145deg, hsl(45 93% 47% / 0.15) 0%, hsl(45 93% 47% / 0.08) 100%)',
                  }}
                >
                  <DeviceMobile className="w-5 h-5 text-amber-500" weight="duotone" />
                </div>
                <div>
                  <div className="font-medium text-foreground">{t('sync.useLocal', 'Заменить облачные')}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {t('sync.useLocalDescription', 'Загрузить локальные данные в облако')}
                  </div>
                </div>
              </button>
            )}
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
